(function () {
  'use strict';
  const hour = 3600000;
  function resolveDeadline(config, now = Date.now()) {
    if (config.mode === 'daily') {
      const day = new Date(now - 3 * hour);
      return Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate() + 1, 3);
    }
    if (config.mode !== 'fixed' || !/(Z|[+-]\d{2}:\d{2})$/.test(config.endAt || '')) return null;
    let result = Date.parse(config.endAt);
    if (!Number.isFinite(result)) return null;
    const loop = Number(config.loopDays) * 24 * hour;
    if (loop > 0 && now >= result) result += Math.ceil((now - result + 1) / loop) * loop;
    return result;
  }
  function remainingParts(deadline, now = Date.now()) {
    const seconds = Math.max(0, Math.ceil((deadline - now) / 1000));
    const pad = (n) => String(n).padStart(2, '0');
    return {hours:pad(Math.floor(seconds / 3600)),minutes:pad(Math.floor(seconds / 60) % 60),seconds:pad(seconds % 60),expired:seconds === 0};
  }
  const sessionMemory = new Map();
  function sessionDeadline(storage, campaign, now = Date.now(), memory = sessionMemory) {
    const key = `alto:black-session:${campaign}:started-at`;
    let start;
    try { start = Number(storage?.getItem(key)); } catch (_) {}
    if (!Number.isFinite(start) || start <= 0 || start > now) start = memory.get(key);
    if (!Number.isFinite(start) || start <= 0 || start > now) {
      start = now;
      try { storage?.setItem(key, String(start)); } catch (_) {}
    }
    memory.set(key, start);
    return start + 15 * 60 * 1000;
  }
  function sessionState(storage, campaign, now = Date.now(), memory = sessionMemory) {
    const initial = sessionDeadline(storage,campaign,now,memory), key = `alto:black-session:${campaign}:extension`;
    let record = memory.get(key);
    try { record = storage?.getItem(key) || record; } catch (_) {}
    if (!record) return {deadline:initial,extended:false,canRestart:now>=initial};
    let start;
    try { start=JSON.parse(record).startedAt; } catch (_) {}
    const valid=Number.isSafeInteger(start)&&start>=initial&&start<=now;
    return {deadline:valid?start+900000:initial,extended:true,canRestart:false};
  }
  function restartSession(storage, campaign, now = Date.now(), memory = sessionMemory) {
    if (!sessionState(storage,campaign,now,memory).canRestart) return null;
    const key=`alto:black-session:${campaign}:extension`, record=JSON.stringify({startedAt:now});
    memory.set(key,record);
    try { storage?.setItem(key,record); } catch (_) {}
    return sessionState(storage,campaign,now,memory);
  }
  function calendarParts(deadline, now = Date.now()) {
    const parts = remainingParts(deadline, now);
    return {...parts, days:String(Math.floor(Number(parts.hours) / 24)).padStart(2,'0'), hours:String(Number(parts.hours) % 24).padStart(2,'0')};
  }
  function savings(price, compareAt) {
    const amount = Math.max(0, Number(compareAt || 0) - Number(price || 0));
    return {amount,percent:amount > 0 ? Math.round(amount / compareAt * 100) : 0};
  }
  function benefitState({enabled, threshold, total}) {
    if (!enabled || !Number.isFinite(threshold) || threshold <= 0 || !Number.isFinite(total)) return {visible:false,remaining:0,progress:0,qualified:false};
    return {visible:true,remaining:Math.max(0,threshold-total),progress:Math.min(100,Math.max(0,total/threshold*100)),qualified:total>=threshold};
  }
  function activationKey(campaign, deadline) { return `alto:black-november:${campaign}:${deadline || 'open'}`; }
  function hasBlockingOverlay(nodes, styleFor) {
    return Array.from(nodes).some(node => {
      if (node.matches('dialog, .drawer')) return node.hasAttribute('open');
      const style = styleFor(node), rect = node.getBoundingClientRect();
      return !node.hidden && style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0' && rect.width > 0 && rect.height > 0;
    });
  }
  function kitItems(ids) {
    if(ids.length!==2 || ids.some(id=>!Number.isSafeInteger(Number(id))||Number(id)<=0))throw new Error('Incomplete kit selection');
    const items=new Map();
    ids.forEach(id=>items.set(Number(id),(items.get(Number(id))||0)+1));
    return Array.from(items,([id,quantity])=>({id,quantity}));
  }
  function blackLead(email,phone,source) {
    const normalizedEmail=String(email||'').trim();
    let digits=String(phone||'').replace(/\D/g,'');
    if(digits.startsWith('55')&&digits.length>11)digits=digits.slice(2);
    if(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)||![10,11].includes(digits.length))throw new Error('Invalid lead');
    return {email:normalizedEmail,phone:'+55'+digits,source};
  }
  function blackLeadParams(lead,config,now=new Date()) {
    const params=new URLSearchParams(),fields=config.fieldNames||{};
    Object.entries(config.activeCampaignHidden||{}).forEach(([key,value])=>{if(value!=='')params.append(key,value);});
    params.append(fields.email||'email',lead.email);
    params.append(fields.name||'fullname','Lead');
    params.append(fields.phone||'phone',lead.phone);
    params.append(fields.campaign||'alto_campaign','alto-black-november-2026');
    params.append(fields.source||'alto_source_url',lead.source);
    String(config.additionalFields||'').split('\n').forEach(line=>{const index=line.indexOf('=');if(index>0){const key=line.slice(0,index).trim();if(key)params.append(key,line.slice(index+1).trim());}});
    params.append('alto_popup_type','black_november');
    params.append('alto_popup_campaign','alto-black-november-2026');
    params.append('alto_popup_coupon','');
    params.append('alto_created_at',now.toISOString());
    new URL(lead.source).searchParams.forEach((value,key)=>{if(/^(utm_|fbclid|gclid|ttclid)/i.test(key))params.append(key,value);});
    params.set('jsonp','true');
    return params;
  }
  function blackMartzPayload(lead,config,now=new Date()) {
    return {email:lead.email,phone:lead.phone,first_name:'Lead',last_name:'Popup',enabled:true,enable_for_interactions:true,integration_id:lead.email||lead.phone,integration_name:config.integrationName||'alto-shopify-popup',tags:['ALTO-BLACK-NOVEMBER-2026-POPUP'],metadata:{source:lead.source,campaign:'alto-black-november-2026',coupon:'',captured_at:now.toISOString()}};
  }
  const core = {resolveDeadline,remainingParts,calendarParts,sessionDeadline,sessionState,restartSession,savings,benefitState,activationKey,kitItems,hasBlockingOverlay,blackLead,blackLeadParams,blackMartzPayload};
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (typeof window === 'undefined' || !window.customElements) return;
  if (!customElements.get('alto-black-visual-gift')) customElements.define('alto-black-visual-gift', class extends HTMLElement {
    connectedCallback() {
      const cartLines=this.dataset.cartLines;
      if(!cartLines)return;
      const key=`alto-black-visual-gift:${this.dataset.campaign}:${cartLines}`;
      const button=this.querySelector('[data-black-gift-claim]'),message=this.querySelector('[data-black-gift-confirmation]');
      const showClaimed=()=>{button.hidden=true;message.hidden=false;};
      try {if(sessionStorage.getItem(key)==='1')showClaimed();}catch(_){}
      button.addEventListener('click',()=>{try{sessionStorage.setItem(key,'1');}catch(_){}showClaimed();});
    }
  });
  if (customElements.get('alto-black-campaign')) return;

  class BlackCampaign extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.dialog = this.querySelector('dialog');
      this.bar = this.querySelector('[data-black-bar]');
      this.started = Date.now();
      let storage;
      try { storage = window.sessionStorage; } catch (_) {}
      this.sessionStorage = storage;
      this.sessionManaged = true;
      this.sessionEnd = sessionState(storage, this.dataset.campaign, this.started).deadline;
      this.dataset.sessionEndAt = String(this.sessionEnd);
      this.memory = new Map();
      this.popupHandled = false;
      this.leadForm = this.querySelector('[data-black-lead-form]');
      const options = {signal:this.abort.signal};
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-black-dismiss-bar]')) { this.bar.hidden = true; this.write('bar', '1'); }
        if (event.target.closest('[data-black-popup-close]')) this.closePopup();
        if (event.target.closest('[data-black-lead-open]')) {
          event.target.closest('[data-black-lead-open]').hidden=true;
          this.querySelector('[data-black-scratch]')?.setAttribute('hidden','');
          this.querySelector('[data-black-scratch-instruction]')?.setAttribute('hidden','');
          this.querySelector('[data-black-reveal]')?.setAttribute('hidden','');
          this.leadForm.hidden=false;
          this.leadForm.elements.email.focus();
        }
        if (event.target === this.dialog) {
          const rect=this.dialog.getBoundingClientRect();
          if(event.clientX<rect.left||event.clientX>rect.right||event.clientY<rect.top||event.clientY>rect.bottom)this.closePopup();
        }
      },options);
      this.leadForm?.addEventListener('submit',event=>{event.preventDefault();void this.submitBlackLead();},options);
      document.addEventListener('click',event=>{
        const button=event.target.closest('[data-black-session-restart]');
        if(!button)return;
        const next=restartSession(this.sessionStorage,this.dataset.campaign);
        if(!next)return;
        this.sessionEnd=next.deadline;this.dataset.sessionEndAt=String(next.deadline);this.tick();
        const offer=button.closest('[data-black-session-offer]');
        offer?.querySelector('[data-black-session-active]')?.focus({preventScroll:true});
        this.emit('alto_black_session_restart');
        document.dispatchEvent(new CustomEvent('alto:black-session-restart'));
      },options);
      this.setupScratch(options);
      this.dialog?.addEventListener('cancel',()=>{this.write('dismissed','1');this.popupHandled=true;},options);
      document.addEventListener('visibilitychange',()=>{if(!document.hidden)this.tick();},options);
      window.addEventListener('pageshow',()=>this.tick(),options);
      this.tick();
      this.interval=setInterval(()=>this.tick(),1000);
    }
    async submitBlackLead() {
      if(this.submittingLead)return;
      const form=this.leadForm,error=form.querySelector('[data-black-lead-error]'),button=form.querySelector('[type=submit]');
      let lead,config;
      try {
        if(form.elements.company.value)return;
        lead=blackLead(form.elements.email.value,form.elements.phone.value,window.location.href);
        config=JSON.parse(document.querySelector('[data-black-lead-config]')?.textContent||'null');
        if(!config?.martz?.enabled||!config.martz.endpoint||!config.martz.apiKey||config.submissionMode!=='active_campaign_form'||!config.endpoint)throw new Error('Missing lead destination');
        if(new URL(config.martz.endpoint).protocol!=='https:'||new URL(config.endpoint).protocol!=='https:')throw new Error('Invalid lead destination');
      } catch(_) {error.textContent=config?.errorText||'Revise seus dados e tente novamente.';error.hidden=false;return;}
      this.submittingLead=true;button.disabled=true;button.setAttribute('aria-busy','true');error.hidden=true;
      try {
        const params=blackLeadParams(lead,config),endpoint=new URL(config.endpoint);
        params.forEach((value,key)=>endpoint.searchParams.append(key,value));
        const results=await Promise.allSettled([
          fetch(config.martz.endpoint,{method:'POST',mode:'cors',credentials:'omit',keepalive:true,headers:{'Content-Type':'application/json','X-API-KEY':config.martz.apiKey},body:JSON.stringify(blackMartzPayload(lead,config.martz))}).then(response=>{if(!response.ok)throw new Error('Martz lead request failed');}),
          fetch(endpoint.href,{method:'GET',mode:'no-cors',credentials:'omit',keepalive:true})
        ]);
        if(results.some(result=>result.status==='rejected'))throw new Error('Lead destination request failed');
        this.write('activated','1');this.closePopup();this.emit('alto_black_activation');
        const grid=document.querySelector('[data-black-products]');
        if(grid){grid.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});grid.focus({preventScroll:true});}
        else window.location.assign(this.dataset.offerUrl||'/collections/produtos');
      } catch(_) {error.textContent=config.errorText||'Não foi possível enviar seus dados. Tente novamente.';error.hidden=false;}
      finally {this.submittingLead=false;button.disabled=false;button.removeAttribute('aria-busy');}
    }
    setupScratch(options) {
      const scratch = this.querySelector('[data-black-scratch]');
      if (!scratch) return;
      const canvas = scratch.querySelector('canvas'), prize = scratch.querySelector('[data-black-prize]');
      const button = this.querySelector('[data-black-reveal]'), result = this.querySelector('[data-black-scratch-result]');
      let revealed = false, pressed = false, previous = null;
      const cells = new Set();
      const reveal = () => {
        if (revealed) return;
        revealed = true;
        scratch.dataset.revealed = 'true';
        prize.removeAttribute('aria-hidden'); prize.removeAttribute('inert');
        canvas.hidden = true; button.hidden = true; result.hidden = false;
        this.querySelector('[data-black-scratch-heading]').textContent = scratch.dataset.revealedLabel;
        result.querySelector('button').focus({preventScroll:true});
        this.emit('alto_black_gift_reveal');
      };
      button.addEventListener('click', reveal, options);
      const draw = () => {
        const rect = canvas.getBoundingClientRect();
        if (!rect.width || !rect.height || revealed) return;
        const ratio = Math.min(devicePixelRatio || 1, 2);
        canvas.width = Math.round(rect.width * ratio); canvas.height = Math.round(rect.height * ratio);
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.scale(ratio,ratio); ctx.globalCompositeOperation = 'source-over';
        const gradient = ctx.createLinearGradient(0,0,rect.width,rect.height);
        gradient.addColorStop(0,'#adb5bf'); gradient.addColorStop(.5,'#e8ebee'); gradient.addColorStop(1,'#929ca9');
        ctx.fillStyle = gradient; ctx.fillRect(0,0,rect.width,rect.height);
        cells.clear();
      };
      this.scratchObserver = new ResizeObserver(draw); this.scratchObserver.observe(canvas);
      const erase = (event) => {
        if (!pressed || revealed) return;
        const rect = canvas.getBoundingClientRect(), ctx = canvas.getContext('2d');
        if (!ctx || !rect.width) return;
        const next = {x:event.clientX-rect.left,y:event.clientY-rect.top}, from = previous || next;
        const distance = Math.hypot(next.x-from.x,next.y-from.y), count = Math.max(1,Math.ceil(distance/8));
        const radius = Math.max(22,rect.width*.1);
        ctx.globalCompositeOperation = 'destination-out';
        for (let i=0;i<=count;i++) {
          const x=from.x+(next.x-from.x)*i/count,y=from.y+(next.y-from.y)*i/count;
          ctx.beginPath();ctx.arc(x,y,radius,0,Math.PI*2);ctx.fill();
          for(let col=0;col<16;col++)for(let row=0;row<10;row++)if(Math.hypot((col+.5)*rect.width/16-x,(row+.5)*rect.height/10-y)<radius)cells.add(col+row*16);
        }
        previous=next;
        if(cells.size>=64)reveal();
      };
      canvas.addEventListener('pointerdown',event=>{pressed=true;previous=null;canvas.setPointerCapture(event.pointerId);erase(event);},options);
      canvas.addEventListener('pointermove',erase,options);
      ['pointerup','pointercancel','lostpointercapture'].forEach(name=>canvas.addEventListener(name,()=>{pressed=false;previous=null;},options));
    }
    disconnectedCallback() { this.scratchObserver?.disconnect(); clearInterval(this.interval);this.abort?.abort();this.abort=null;this.dialog?.close(); }
    read(name) { try {return sessionStorage.getItem(`${this.key}:${name}`)||this.memory.get(`${this.key}:${name}`);}catch(_){return this.memory.get(`${this.key}:${name}`);} }
    write(name,value) {this.memory.set(`${this.key}:${name}`,value);try{sessionStorage.setItem(`${this.key}:${name}`,value);}catch(_){} }
    suppressed() {return window.altoScratchIsSuppressed?.() || document.documentElement.getAttribute('data-alto-scratch-suppressed')==='true';}
    emit(name) {window.dataLayer?.push({event:name,alto_campaign:this.dataset.campaign,theme_experience:'black-november-play'});}
    closePopup() {this.popupHandled=true;this.write('dismissed','1');this.dialog?.close();}
    tick() {
      const deadline=resolveDeadline({mode:this.dataset.clockMode,endAt:this.dataset.endAt,loopDays:this.dataset.loopDays});
      this.key=activationKey(this.dataset.campaign,deadline);
      const parts=deadline ? calendarParts(deadline) : null;
      const state=this.sessionManaged?sessionState(this.sessionStorage,this.dataset.campaign):{deadline:this.sessionEnd,extended:false,canRestart:true};
      this.sessionEnd=state.deadline;this.dataset.sessionEndAt=String(this.sessionEnd);
      const sessionParts=remainingParts(this.sessionEnd);
      document.querySelectorAll('[data-black-clock]').forEach(clock=>{
        const local=clock.dataset.blackClockScope==='session';
        const current=local ? sessionParts : parts;
        clock.hidden=!current || current.expired;
        if(!current)return;
        ['days','hours','minutes','seconds'].forEach(part=>{const n=clock.querySelector(`[data-black-${part}]`);if(n)n.textContent=current[part];});
      });
      document.querySelectorAll('[data-black-session-offer]').forEach(offer=>{
        offer.hidden=false;
        offer.dataset.sessionEnded=String(sessionParts.expired);
        offer.querySelector('[data-black-session-active]').hidden=sessionParts.expired;
        offer.querySelector('[data-black-session-expired]').hidden=!sessionParts.expired;
        const retry=offer.querySelector('[data-black-session-retry]'),final=offer.querySelector('[data-black-session-final]');
        if(retry)retry.hidden=!state.canRestart;
        if(final)final.hidden=!state.extended;
      });
      document.querySelectorAll('[data-black-expired]').forEach(n=>n.hidden=!parts?.expired);
      if(this.bar)this.bar.hidden=this.read('bar')==='1';
      if(parts?.expired){this.dialog?.close();return;}
      if(!this.dialog || this.popupHandled || this.dataset.popup!=='true' || this.suppressed() || this.read('activated') || this.read('dismissed'))return;
      if(document.hidden || Date.now()-this.started<Number(this.dataset.delay||14)*1000)return;
      if(hasBlockingOverlay(document.querySelectorAll('dialog[open], .drawer[open], [role="dialog"][aria-modal="true"]'),getComputedStyle))return;
      this.popupHandled=true;
      this.dialog.showModal();
      this.emit('alto_black_popup_view');
    }
  }
  customElements.define('alto-black-campaign', BlackCampaign);

  const stockDismissed = new Set();
  class BlackStockNotice extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.key = `alto:black-stock:${this.dataset.campaign}:${this.dataset.variant}`;
      this.update = () => {
        const dismissed = stockDismissed.has(this.key);
        let stored = false;
        try { stored = sessionStorage.getItem(this.key) === '1'; } catch (_) {}
        const blocked = document.hidden || hasBlockingOverlay(document.querySelectorAll('dialog[open], .drawer[open], [role="dialog"][aria-modal="true"]'),getComputedStyle);
        this.hidden = dismissed || stored || Boolean(blocked);
        if (this.hidden) return;
        let bottom = 16;
        document.querySelectorAll('.alto-whatsapp-float, product-quick-add, [id^="alto-collection-sticker-"]').forEach(control => {
          const rect = control.getBoundingClientRect();
          const style = getComputedStyle(control);
          if (style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.top < innerHeight && rect.bottom > 0) bottom = Math.max(bottom, innerHeight - rect.top + 12);
        });
        this.style.setProperty('--stock-bottom', `${bottom}px`);
      };
      this.querySelector('[data-stock-close]').addEventListener('click', () => {
        stockDismissed.add(this.key);
        try { sessionStorage.setItem(this.key, '1'); } catch (_) {}
        this.hidden = true;
      }, {signal:this.abort.signal});
      window.addEventListener('resize', this.update, {signal:this.abort.signal});
      window.addEventListener('scroll', this.update, {passive:true, signal:this.abort.signal});
      document.addEventListener('visibilitychange', this.update, {signal:this.abort.signal});
      this.observer = new MutationObserver(this.update);
      this.observer.observe(document.body, {subtree:true, attributes:true, attributeFilter:['open','aria-modal']});
      document.querySelectorAll('[role="dialog"][aria-modal="true"]').forEach(modal => this.observer.observe(modal,{attributes:true,attributeFilter:['class','style']}));
      this.delay = setTimeout(this.update, 1500);
    }
    disconnectedCallback() { clearTimeout(this.delay); this.abort?.abort(); this.observer?.disconnect(); }
  }
  customElements.define('alto-black-stock-notice', BlackStockNotice);

  class BlackKit extends HTMLElement {
    connectedCallback() {
      if(this.abort)return;
      this.abort=new AbortController();
      this.form=this.querySelector('form');this.button=this.querySelector('[type="submit"]');this.message=this.querySelector('[data-kit-message]');
      this.form.addEventListener('change',()=>this.updateTotal(),{signal:this.abort.signal});
      this.form.addEventListener('submit',event=>{event.preventDefault();this.addItems();},{signal:this.abort.signal});
      this.updateTotal();
    }
    disconnectedCallback(){this.abort?.abort();this.abort=null;}
    updateTotal(){
      const selects=Array.from(this.querySelectorAll('select'));
      const complete=selects.every(s=>s.value);
      const total=selects.reduce((sum,s)=>sum+Number(s.selectedOptions[0]?.dataset.price||0),0);
      const output=this.querySelector('[data-kit-total]');
      output.hidden=!complete;
      output.textContent=new Intl.NumberFormat('pt-BR',{style:'currency',currency:this.dataset.currency||'BRL'}).format(total/100);
    }
    async addItems(){
      if(this.busy || !this.form.reportValidity())return;
      let items;try{items=kitItems(Array.from(this.querySelectorAll('select'),s=>s.value));}catch(_){this.message.textContent=this.dataset.incomplete;return;}
      this.busy=true;this.button.disabled=true;this.form.setAttribute('aria-busy','true');this.message.textContent=this.dataset.loading;
      try{
        const base=window.Shopify?.routes?.root || '/';
        const response=await fetch(`${base}cart/add.js`,{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items})});
        const result=await response.json();
        if(!response.ok || !Array.isArray(result.items) || result.items.length!==items.length)throw new Error('Unconfirmed kit response');
        this.message.textContent=this.dataset.success;
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        const drawer=document.getElementById('cart-drawer');
        if(drawer?.show)drawer.show();
        window.dataLayer?.push({event:'alto_black_kit_added',items:items.map(i=>({item_variant:i.id,quantity:i.quantity}))});
      }catch(_){this.message.textContent=this.dataset.error;}
      finally{this.busy=false;this.button.disabled=false;this.form.removeAttribute('aria-busy');}
    }
  }
  if(!customElements.get('alto-black-kit'))customElements.define('alto-black-kit',BlackKit);
})();
